// src/components/customer/ProductsDashboard.jsx
import React from "react";
import { LogOut, ExternalLink, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../shared/ui/LanguageSwitcher";

const IMAGE_BASE = (process.env.REACT_APP_API_URL || "/api")
  .replace(/\/+$/, "")
  .replace(/\/api$/i, "");

/**
 * Extract + normalize product URL in a tolerant way
 * (supports different backend property names + adds https:// if missing).
 */
const getProductUrl = (p) => {
  const raw =
    p?.productUrl ??
    p?.url ??
    p?.productURL ??
    p?.productLink ??
    p?.link ??
    "";

  const u = String(raw || "").trim();
  if (!u) return "";

  // If missing scheme, assume https
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u}`;
};

const ProductsDashboard = ({ products = [], onSelectProduct, onLogout }) => {
  const { t } = useTranslation();
  const noProducts = !Array.isArray(products) || products.length === 0;

  const handleOpen = (product) => {
    // Optional override from parent
    if (typeof onSelectProduct === "function") {
      onSelectProduct(product);
      return;
    }

    const url = getProductUrl(product);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src="/login-bg.mp4" type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-10">
        <div
          className="w-full max-w-6xl bg-white/10 backdrop-blur-xl 
          rounded-3xl shadow-[0_0_60px_rgba(255,0,255,0.25)] border border-white/20 p-8"
        >
          <div className="flex flex-wrap justify-between items-center gap-4 mb-10">
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              <h1 className="text-3xl font-bold drop-shadow-md">
                {t("customer.productsDashboard.title")}
              </h1>
            </div>

            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-5 py-3 rounded-xl 
              text-white font-semibold bg-gradient-to-r from-pink-500 to-red-500 
              hover:opacity-90 transition"
            >
              <LogOut className="w-5 h-5" />
              {t("common.logout")}
            </button>
          </div>

          {noProducts && (
            <div className="text-center py-16">
              <Package className="w-20 h-20 mx-auto text-gray-300 mb-6" />
              <h2 className="text-2xl text-gray-200 font-semibold">
                {t("customer.productsDashboard.noProductsTitle")}
              </h2>
              <p className="mt-2 text-gray-300">
                {t("customer.productsDashboard.noProductsSubtitle")}
              </p>
            </div>
          )}

          {!noProducts && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => {
                const url = getProductUrl(product);
                const disabled = !url;

                return (
                  <div
                    key={product.productId ?? product.id}
                    className="p-6 rounded-2xl 
                      bg-white/10 border border-white/20 backdrop-blur-xl
                      hover:bg-white/20 hover:shadow-2xl transition-all"
                  >
                    <div className="text-center">
                      {product.productImage ? (
                        <img
                          src={`${IMAGE_BASE}${product.productImage}`}
                          alt={product.productName}
                          className="w-24 h-24 object-cover rounded-xl mx-auto mb-4 shadow-lg"
                        />
                      ) : (
                        <Package className="w-20 h-20 mx-auto text-gray-300 mb-4" />
                      )}

                      <h3 className="text-xl font-bold text-white drop-shadow mb-2">
                        {product.productName}
                      </h3>

                      {typeof product.remainingDays === "number" &&
                        product.remainingDays >= 0 &&
                        product.remainingDays <= 15 && (
                          <div className="mt-3 p-2 bg-yellow-500/20 border border-yellow-400/40 rounded-lg">
                            <p className="text-xs text-yellow-200">
                              {t("customer.productsDashboard.remainingDaysWarning", {
                                days: product.remainingDays,
                              })}
                            </p>
                          </div>
                        )}

                      {/* ✅ Open product */}
                      {disabled ? (
                        <div className="mt-5 text-sm text-gray-300">
                          Link not available
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpen(product)}
                          className="mt-5 inline-flex items-center justify-center gap-2 
                            text-pink-300 font-semibold hover:text-white transition"
                        >
                          <ExternalLink className="w-5 h-5" />
                          <span>{t("customer.productsDashboard.openProduct")}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsDashboard;
